import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import { keyframes } from '@emotion/react/macro';
import { ArrowDownIcon, ArrowUpIcon, GrabHandleIcon, theme, withAlpha } from '@arnold/common';
import { useTranslation } from 'react-i18next';
import { IInputContextValue, InputConsumer } from '../chat/InputContext';
import { hidePipeSymbols } from '../lib/questionText';
import { Button } from './Button';
import { createDeterministicOrder, rankingOrderToSelections, rankingSelectionsToOrder } from './scaleStatementOrder';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 930px;
  max-width: 100%;
  margin: 0 auto;
  padding: 8px;
`;

const Instructions = styled.p`
  margin: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.medium.regular.fontSize};
`;

const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const movedHighlight = keyframes`
  0%, 45% {
    background: ${withAlpha(theme.colors.actionPrimary.default, 0.08)};
  }

  100% {
    background: ${theme.colors.backgroundBasic.default};
  }
`;

const Row = styled.li<{ isDragging: boolean; wasMoved: boolean }>`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  min-height: 64px;
  border: 1px solid
    ${({ isDragging }) => (isDragging ? theme.colors.actionPrimary.default : theme.colors.borderMain.default)};
  border-radius: 8px;
  background: ${theme.colors.backgroundBasic.default};
  box-shadow: ${({ isDragging }) => (isDragging ? '0 18px 40px rgba(0, 0, 0, 0.24)' : 'none')};
  opacity: 1;
  transform: ${({ isDragging }) => (isDragging ? 'scale(1.015)' : 'scale(1)')};
  animation: ${({ wasMoved, isDragging }) => (!isDragging && wasMoved ? movedHighlight : 'none')} 1100ms ease-out;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease,
    transform 120ms ease;
`;

const DragHandle = styled.button`
  display: grid;
  place-items: center;
  width: 44px;
  min-height: 64px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${theme.colors.iconText.secondary};
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.default};
    outline-offset: -3px;
    border-radius: 8px;
  }
`;

const Rank = styled.span`
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  margin-right: 12px;
  border-radius: 50%;
  color: ${theme.colors.textInverted.primary};
  background: ${theme.colors.actionPrimary.default};
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
`;

const Statement = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 12px 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.medium.regular.fontSize};
`;

const MoveButtons = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
`;

const MoveButton = styled.button`
  display: grid;
  place-items: center;
  width: 36px;
  height: 28px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: ${theme.colors.iconText.secondary};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${theme.colors.iconAction.primary};
    background: ${theme.colors.backgroundBasic.hover};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.default};
    outline-offset: -2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  border-radius: 19px;
  padding: 8px 24px;
  font-size: ${theme.typography.body.medium.button.fontSize};
  font-weight: ${theme.typography.body.medium.button.fontWeight};
  color: ${theme.colors.textInverted.primary};
  background-color: ${theme.colors.actionPrimary.default};

  &:disabled {
    background-color: ${theme.colors.backgroundBasic.disabled};
    color: ${theme.colors.actionPrimary.disabled};
    cursor: not-allowed;
  }
`;

interface ScaleRankingListProps {
  statements: string[];
  randomizeOrder?: boolean;
  seedKey: string;
  disabled?: boolean;
  onSubmit?: (selections: number[]) => void;
}

interface ScaleRankingListInnerProps extends ScaleRankingListProps {
  context: IInputContextValue;
}

type DragState = {
  index: number;
  pointerId: number;
  startY: number;
  isDragging: boolean;
  hasMoved: boolean;
};

const ScaleRankingListInner: React.FC<ScaleRankingListInnerProps> = ({
  statements,
  randomizeOrder,
  seedKey,
  disabled,
  onSubmit,
  context,
}) => {
  const { t } = useTranslation('scaleStatements');
  const { state, initScaleState, setScaleSelection, submitScaleAnswers } = context;
  const initialOrder = useMemo(
    () =>
      randomizeOrder && statements.length > 1
        ? createDeterministicOrder(statements.length, seedKey)
        : statements.map((_statement, index) => index),
    [randomizeOrder, seedKey, statements],
  );
  const prefilledOrder = useMemo(
    () =>
      state.prefilledScaleSelections?.length === statements.length
        ? rankingSelectionsToOrder(state.prefilledScaleSelections)
        : null,
    [state.prefilledScaleSelections, statements.length],
  );
  const [orderedStatementIndexes, setOrderedStatementIndexes] = useState(initialOrder);
  const [isInitialized, setIsInitialized] = useState(false);
  const dragState = useRef<DragState | null>(null);
  const movedHighlightTimeout = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [movedIndex, setMovedIndex] = useState<number | null>(null);

  const synchronizeSelections = useCallback(
    (order: number[]) => {
      rankingOrderToSelections(order).forEach((rankIndex, statementIndex) =>
        setScaleSelection(statementIndex, rankIndex),
      );
    },
    [setScaleSelection],
  );

  const displayedOrder = prefilledOrder ?? initialOrder;

  useEffect(() => {
    initScaleState(statements.length);
    setOrderedStatementIndexes(displayedOrder);
    synchronizeSelections(displayedOrder);
    setIsInitialized(true);
  }, [displayedOrder, initScaleState, statements.length, synchronizeSelections]);

  useEffect(
    () => () => {
      if (movedHighlightTimeout.current != null) {
        window.clearTimeout(movedHighlightTimeout.current);
      }
    },
    [],
  );

  const highlightMovedStatement = (index: number) => {
    if (movedHighlightTimeout.current != null) {
      window.clearTimeout(movedHighlightTimeout.current);
    }
    setMovedIndex(null);
    window.requestAnimationFrame(() => setMovedIndex(index));
    movedHighlightTimeout.current = window.setTimeout(() => setMovedIndex(null), 1100);
  };

  const moveStatement = (from: number, to: number, highlightAfterMove = false) => {
    if (from === to || to < 0 || to >= orderedStatementIndexes.length) {
      return;
    }
    const nextOrder = [...orderedStatementIndexes];
    const [statementIndex] = nextOrder.splice(from, 1);
    nextOrder.splice(to, 0, statementIndex);
    setOrderedStatementIndexes(nextOrder);
    synchronizeSelections(nextOrder);
    if (dragState.current?.isDragging) {
      setDraggingIndex(to);
      dragState.current.index = to;
      dragState.current.hasMoved = true;
    } else if (highlightAfterMove) {
      highlightMovedStatement(to);
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>, index: number) => {
    if (disabled) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = {
      index,
      pointerId: event.pointerId,
      startY: event.clientY,
      isDragging: false,
      hasMoved: false,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const currentDrag = dragState.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) {
      return;
    }
    if (!currentDrag.isDragging && Math.abs(event.clientY - currentDrag.startY) < 6) {
      return;
    }
    currentDrag.isDragging = true;
    event.preventDefault();
    setDraggingIndex(currentDrag.index);

    const target = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>('[data-ranking-index]');
    const targetIndex = target?.dataset.rankingIndex ? Number(target.dataset.rankingIndex) : NaN;
    if (Number.isInteger(targetIndex)) {
      moveStatement(currentDrag.index, targetIndex);
    }
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragState.current?.pointerId !== event.pointerId) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const completedDrag = dragState.current;
    dragState.current = null;
    setDraggingIndex(null);
    if (completedDrag.isDragging && completedDrag.hasMoved) {
      highlightMovedStatement(completedDrag.index);
    }
  };

  const handleSubmit = async () => {
    onSubmit?.(rankingOrderToSelections(orderedStatementIndexes));
    await submitScaleAnswers();
  };

  return (
    <Container>
      <Instructions>{t('rankingInstructions')}</Instructions>
      <List aria-label={t('rankingInstructions')}>
        {orderedStatementIndexes.map((statementIndex, rankIndex) => {
          const statement = hidePipeSymbols(statements[statementIndex] ?? '');
          return (
            <Row
              key={statementIndex}
              data-ranking-index={rankIndex}
              isDragging={draggingIndex === rankIndex}
              wasMoved={movedIndex === rankIndex}
              aria-label={t('rankingPosition', { position: rankIndex + 1 })}
            >
              <DragHandle
                type="button"
                aria-label={t('rankingDragInstructions')}
                disabled={disabled}
                onPointerDown={(event) => handlePointerDown(event, rankIndex)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
              >
                <GrabHandleIcon />
              </DragHandle>
              <Statement>
                <Rank aria-hidden="true">{rankIndex + 1}</Rank>
                {statement}
              </Statement>
              <MoveButtons>
                <MoveButton
                  type="button"
                  aria-label={t('moveStatementUp', { statement })}
                  disabled={disabled || rankIndex === 0}
                  onClick={() => moveStatement(rankIndex, rankIndex - 1, true)}
                >
                  <ArrowUpIcon />
                </MoveButton>
                <MoveButton
                  type="button"
                  aria-label={t('moveStatementDown', { statement })}
                  disabled={disabled || rankIndex === orderedStatementIndexes.length - 1}
                  onClick={() => moveStatement(rankIndex, rankIndex + 1, true)}
                >
                  <ArrowDownIcon />
                </MoveButton>
              </MoveButtons>
            </Row>
          );
        })}
      </List>
      <SubmitButton onClick={handleSubmit} disabled={disabled || !isInitialized}>
        {t('submit')}
      </SubmitButton>
    </Container>
  );
};

const ScaleRankingList: React.FC<ScaleRankingListProps> = (props) => (
  <InputConsumer>{(context) => <ScaleRankingListInner {...props} context={context} />}</InputConsumer>
);

export default ScaleRankingList;
